import { createContext, useContext, useState, useEffect, useCallback } from "react";
import API from "./api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [sessionId, setSessionId] = useState(null);

  const fetchUser = useCallback(async (retryCount = 0) => {
    try {
      // httpOnly cookie is sent automatically — no need for localStorage token
      const r = await API.get("/auth/me");
      setUser(r.data);
      setLoading(false);
      // Migrate: clear any legacy localStorage token
      localStorage.removeItem("mep_token");
      // Check onboarding for directors
      if (r.data.role === "Director" || r.data.role === "Admin") {
        try {
          const ob = await API.get("/onboarding/status");
          setNeedsOnboarding(!ob.data?.completed);
        } catch { setNeedsOnboarding(false); }
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        // Auth cookie invalid or missing — clear legacy token and reset
        localStorage.removeItem("mep_token");
        delete API.defaults.headers.common["Authorization"];
        setUser(null);
        setLoading(false);
      } else if (retryCount < 2) {
        // Network error / 502 / pod restart — retry after delay (up to 2 retries)
        await new Promise((r) => setTimeout(r, 1500 * (retryCount + 1)));
        return fetchUser(retryCount + 1);
      } else {
        // Exhausted retries — backend may be down
        setUser(null);
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => { fetchUser(); }, [fetchUser]);

  const login = async (email, password) => {
    const r = await API.post("/auth/login", { email, password });
    // Backend sets httpOnly cookie automatically — no localStorage needed
    if (r.data.session_id) setSessionId(r.data.session_id);
    await fetchUser();
  };

  const loginWithToken = async (token, userData) => {
    // For OAuth flows: set Authorization header in memory for the initial /auth/me call
    // Backend cookie will be established on next authenticated request
    API.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    await fetchUser();
    // Clean up in-memory header once cookie is established
    delete API.defaults.headers.common["Authorization"];
  };

  const logout = async () => {
    try { await API.post("/auth/logout"); } catch {}
    // Clear any legacy localStorage data
    localStorage.removeItem("mep_token");
    delete API.defaults.headers.common["Authorization"];
    setUser(null);
    setSessionId(null);
  };

  // Tier-based access helpers
  const tier = user?.permission_tier ?? (user?.role === "Director" || user?.role === "Admin" ? 5 : 1);
  const isDirector = user?.role === "Director" || user?.role === "Admin";
  const tabAccess = new Set(user?.tab_access || (isDirector ? ["timesheets","scope","files","overview","risk","pricing","approval","proposals"] : ["timesheets"]));
  const permissions = new Set(user?.permissions || []);
  const canCreateProjects = user?.can_create_projects || isDirector;

  return (
    <AuthContext.Provider value={{
      user, loading, login, loginWithToken, logout, fetchUser,
      isDirector,
      needsOnboarding,
      sessionId,
      tier,
      tabAccess,
      permissions,
      canCreateProjects,
      canViewTab: (tab) => tabAccess.has(tab),
      hasPermission: (perm) => permissions.has(perm) || isDirector,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
}
